'use client';

/**
 * Провайдер авторизации.
 *
 * Кроме собственно OIDC делает одну важную вещь: связывает токен с
 * HTTP-клиентом. Клиент про OIDC не знает — он спрашивает токен у функции,
 * и подменить её здесь дешевле, чем протаскивать токен через каждый вызов.
 */

import React, { useEffect } from 'react';
import { AuthProvider as OidcAuthProvider, useAuth } from 'react-oidc-context';
import type { User } from 'oidc-client-ts';
import { setTokenProvider } from '@/lib/api/client';
import { oidcSettings, type SigninState } from './config';

/** Куда вернуться после входа. Читается из state, который мы сами и положили. */
function returnToOf(user: User | void): string {
  const state = user?.state as SigninState | undefined;
  const returnTo = state?.returnTo;
  // Принимаем только внутренний путь: чужой absolute URL из state — это
  // открытый редирект, и подставить его может кто угодно ссылкой.
  if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) return returnTo;
  return '/dashboard';
}

/**
 * После обмена кода на токен убираем `code` и `state` из адреса и уводим
 * человека туда, откуда его завернули на вход. Полная перезагрузка здесь
 * уместна: адрес с кодом не должен остаться в истории браузера.
 */
function onSigninCallback(user: User | void) {
  window.location.replace(returnToOf(user));
}

/**
 * Текущий токен.
 *
 * Живёт в модуле, а не в состоянии, и обновляется на рендере, а не в
 * эффекте. Причина конкретная: эффекты дочерних компонентов React выполняет
 * раньше родительских, так что первый запрос успевал уйти до того, как мост
 * вставал на место, — и падал «нет сессии» при живом входе.
 */
let currentAccessToken: string | null = null;

setTokenProvider(() => currentAccessToken);

/** Держит провайдер токена в HTTP-клиенте в согласии с текущей сессией. */
function TokenBridge({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  // Присваивание на рендере намеренно: значение идемпотентно и должно быть
  // готово раньше любого эффекта ниже по дереву.
  currentAccessToken = auth.user?.access_token ?? null;

  useEffect(() => {
    /**
     * Токен протух. Сессия в Zitadel обычно ещё жива, так что повторный
     * вход проходит без ввода пароля — человек видит короткий переход,
     * а не 401 посреди работы.
     */
    return auth.events.addAccessTokenExpired(() => {
      void auth.signinRedirect({
        state: {
          returnTo: window.location.pathname + window.location.search,
        } satisfies SigninState,
      });
    });
  }, [auth]);

  return <>{children}</>;
}

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <OidcAuthProvider {...oidcSettings} onSigninCallback={onSigninCallback}>
      <TokenBridge>{children}</TokenBridge>
    </OidcAuthProvider>
  );
}
